/**
 * Night mobs: the ones that hunt you.
 *
 * Shares the shape of the fauna AI (entity list, per-frame update, sprite state
 * machine) but the behaviour is inverted — instead of fleeing the player it
 * chases, and instead of wandering forever it de-aggros and goes home. The two
 * were NOT merged into one module: an animal that runs and a mob that hunts share
 * almost no decision logic, and one generic "creature" would be a mass of
 * `if hostile` branches. They share `nav.ts` (built explicitly for every walking
 * agent) and the sprite conventions, which is the part worth sharing.
 *
 * Server owns the roll and the eligibility gates (mobs.service.ts); this owns
 * spawning, movement, attacking, and the dawn dissolve. Damage to the player is
 * client-reported — the honest limit noted everywhere else applies.
 *
 * Sprite convention: mobs are humanoid → down/side/up (the player's order), NOT
 * the animals' side/down/up.
 */
import { Container, Sprite, Texture, Rectangle } from 'pixi.js';
import { TILE } from './world';
import { findRoute, type NavGrid } from './nav';

type Facing = 'down' | 'side' | 'up';
type State = 'spawning' | 'idle' | 'chase' | 'attack' | 'return' | 'dying';

export interface MobStat {
  id: string;
  name: string;
  hp: number;
  damage: number;
  speed: number;
  aggroTiles: number;
  behaviour: 'melee' | 'ranged' | 'dasher' | 'kiter' | 'chief';
  biome?: 'meadow' | 'volcano';
  ranged?: { range: number; everyMs: number; damage: number };
  dash?: { tiles: number; everyMs: number };
  callsAdds?: number;
}

export interface MobTuning {
  litZoneTiles: number;
  spawnMinTiles: number;
  spawnMaxTiles: number;
  baseSpawnRate: number;
  spawnEveryMs: number;
  deAggroTiles: number;
  loseTargetMs: number;
  dissolveMs: number;
  maxAlive: number;
}

/**
 * Fallback tuning, used only until the server's MOB_AI arrives. It MUST mirror
 * `backend/src/game/mobs/mobs.config.ts` — these are the same numbers, not an
 * independent client balance choice.
 *
 * deAggroTiles 15→9 and loseTargetMs 3000→1200 are the aggression cut: chase
 * persistence, not raw speed, is what decides whether a player can walk away from
 * a fight. At 15 tiles a mob followed you most of a screen and picked up every
 * other mob on the route.
 */
export const DEFAULT_MOB_TUNING: MobTuning = {
  litZoneTiles: 10, spawnMinTiles: 12, spawnMaxTiles: 26,
  baseSpawnRate: 1, spawnEveryMs: 5000, deAggroTiles: 9, loseTargetMs: 1200,
  dissolveMs: 2000, maxAlive: 6,
};

export interface MobEntity {
  id: string;
  stat: MobStat;
  sprite: Sprite;
  hp: number;
  state: State;
  x: number; y: number;
  spawnX: number; spawnY: number;   // where it appeared → where it returns
  facing: Facing;
  flipped: boolean;
  path: { x: number; y: number }[];
  waypoint: { x: number; y: number } | null;
  animT: number; frame: number;
  curAnim: string;                  // the anim currently playing → reset frames when it changes
  moved: boolean;                   // actually stepped this frame → drives walk vs idle (no marching in place)
  hitMs: number;
  dyingMs: number;
  killed: boolean;                  // died from a hit (fall + fade) vs dawn dissolve
  spawnMs: number;                  // flicker-in on appearance
  attackCdMs: number;               // ranged/melee cooldown
  loseMs: number;                   // countdown after losing the target
  attacking: boolean;               // mid-attack-animation (don't move)
  dashCdMs: number;                 // dasher: time until the next lunge is allowed
  dashMs: number;                   // dasher: remaining duration of the current lunge (>0 = dashing)
}

export interface MobTextures {
  get(species: string, anim: string, dir: Facing): Texture[] | null;
}

/**
 * Per-species animation table: which clips exist and how many frames each has.
 * Module-level (not inside the loader) so the sprite-existence test can derive the
 * exact file-name set the renderer will ask for instead of trusting the directory.
 */
export const MOB_ANIMS: Record<string, { anim: string; frames: number }[]> = {
  skeleton: [
    { anim: 'idle', frames: 6 }, { anim: 'walk', frames: 6 },
    { anim: 'shoot', frames: 6 }, { anim: 'hit', frames: 4 },
  ],
  skeleton_archer: [
    { anim: 'idle', frames: 6 }, { anim: 'walk', frames: 6 },
    { anim: 'shoot', frames: 6 }, { anim: 'hit', frames: 4 },
  ],
  orc: [
    { anim: 'idle', frames: 6 }, { anim: 'walk', frames: 6 },
    { anim: 'attack', frames: 8 }, { anim: 'hit', frames: 4 },
  ],
  orc_chief: [
    { anim: 'idle', frames: 6 }, { anim: 'walk', frames: 6 },
    { anim: 'attack', frames: 8 }, { anim: 'hit', frames: 4 },
  ],
  cowling: [
    { anim: 'idle', frames: 4 }, { anim: 'walk', frames: 6 },
    { anim: 'attack', frames: 4 }, { anim: 'hit', frames: 2 },
  ],
  flying_skull: [
    { anim: 'idle', frames: 3 }, { anim: 'walk', frames: 3 },
    { anim: 'attack', frames: 3 }, { anim: 'hit', frames: 2 },
  ],
};

/** Loads only the species that can currently spawn. */
export async function loadMobTextures(ids: string[]): Promise<MobTextures> {
  const { Assets } = await import('pixi.js');
  const DIRS: Facing[] = ['down', 'side', 'up'];
  const urls: Record<string, string> = {};
  for (const id of ids) {
    for (const { anim, frames } of MOB_ANIMS[id] ?? []) {
      for (const d of DIRS) for (let f = 0; f < frames; f++) {
        urls[`${id}_${anim}_${d}_${f}`] = `/sprites/mobs/${id}_${anim}_${d}_${f}.png`;
      }
    }
    // death is unfaced (one collapse)
    for (let f = 0; f < 4; f++) urls[`${id}_death_${f}`] = `/sprites/mobs/${id}_death_${f}.png`;
  }
  const loaded = (await Assets.load(Object.values(urls))) as Record<string, Texture>;
  const tex: Record<string, Texture> = {};
  for (const [k, u] of Object.entries(urls)) tex[k] = loaded[u] ?? Texture.from(u);
  return {
    get(species, anim, dir) {
      const out: Texture[] = [];
      const faced = anim !== 'death';
      for (let f = 0; ; f++) {
        const t = tex[faced ? `${species}_${anim}_${dir}_${f}` : `${species}_${anim}_${f}`];
        if (!t) break;
        out.push(t);
      }
      return out.length ? out : null;
    },
  };
}

const ANIM_FPS = { idle: 4, walk: 9, attack: 12, hit: 12, death: 8 } as const;
const HIT_FLASH_MS = 220;
const SPAWN_FLICKER_MS = 900;
// Dasher lunge. Was 2.1× for 520ms on top of a 3.8 t/s orc — a burst of ~8 t/s,
// faster than a SPRINTING player (GAIT.run = 6.5), so the lunge was unavoidable
// rather than a threat to read. 1.55× for 420ms on the new 2.5 t/s orc peaks at
// ~3.9 t/s: it closes ground on a walker, and a running player still escapes it.
const DASH_MULT = 1.55;
const DASH_DURATION_MS = 420;
// Telegraphed wind-up before damage lands. 250→380ms: the tell has to outlast a
// human reaction time (~250ms) for "dodge it" to be a real option rather than a
// coin flip, and the mobs are slow enough now that stepping out actually works.
const WINDUP_MS = 380;
const DEATH_MS = 1300;          // a killed mob: fall + lie collapsed, then fade out
const MOB_VISIBLE_TILES = 11;   // only render a mob within the lit area (fog visible radius)
// Scale mob art to roughly the hero's silhouette, with per-species tweaks. The mob
// frames are 64px (like the hero) but PX below divides by 32, so the effective frame
// scale is PX·MULT·SPECIES = 1.5·MULT·SPECIES vs the hero's CHAR_SCALE ≈ 2.4. At
// MULT 2.2 orcs rendered ~3.7 — 3–4× the hero; 1.5 lands a plain skeleton ≈ hero,
// orcs a touch bulkier, the chief clearly bigger.
const MOB_HERO_MULT = 1.5;
const MOB_SCALE: Record<string, number> = { orc: 1.12, orc_chief: 1.3, skeleton_archer: 0.95, cowling: 0.85, flying_skull: 0.8 };

/** Fired when a mob's attack lands on the player, or when one dies. */
export interface MobEvents {
  onHitPlayer: (damage: number) => void;
  onDeath: (m: MobEntity) => void;
  /** The chief calling adds — returns spawn points near it. */
  onCallAdds?: (m: MobEntity, count: number) => void;
  /**
   * A mob just noticed the player and started chasing (idle → chase). Fired once
   * per aggro, not per frame, so it can carry a growl without the caller having to
   * de-duplicate a state it can't see.
   */
  onAggro?: (m: MobEntity) => void;
  /** A melee mob started its swing — fired on the wind-up, before damage lands. */
  onAttack?: (m: MobEntity) => void;
  /** §7: a ranged mob released a shot — spawn a projectile that deals `damage` on hit. */
  onFire?: (m: MobEntity, damage: number) => void;
}

export interface Mobs {
  entities: MobEntity[];
  /** Spawn `n` mobs of `stat` around the player, avoiding lit zones. */
  spawn(stat: MobStat, n: number, playerX: number, playerY: number, litZones: { x: number; y: number }[], grid: NavGrid): void;
  /** Per frame. `isDark` false (dawn) triggers the dissolve. */
  /**
   * `safe` — the hero is standing inside his tent's safe zone. Authoritative rules
   * live server-side in backend/src/game/field/tent.ts; this is the client acting on
   * the same answer so the world visibly agrees with it.
   */
  update(dt: number, playerX: number, playerY: number, isDark: boolean, grid: NavGrid, now: number, fearless?: boolean, safe?: boolean): void;
  hitAt(wx: number, wy: number, damage: number): { entity: MobEntity; died: boolean } | null;
  count(): number;
  destroy(): void;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

export function createMobs(
  tex: MobTextures,
  events: MobEvents,
  tuning: MobTuning = DEFAULT_MOB_TUNING,
  host?: Container,
): Mobs {
  // Mobs mount into the shared y-sorted entity layer (host) so they sort with
  // trees/rocks/the hero by feet-Y instead of a nested container pinned behind all
  // decor. Falls back to an own container if no host is given.
  const ownLayer = host ?? new Container();
  ownLayer.sortableChildren = true;
  const mount = ownLayer;
  const entities: MobEntity[] = [];
  const PX = TILE / 32;
  let seq = 0;

  const anims = (m: MobEntity): string => {
    if (m.state === 'dying') return 'death';
    if (m.hitMs > 0) return 'hit';
    if (m.attacking) return m.stat.behaviour === 'ranged' || m.stat.behaviour === 'kiter' ? 'shoot' : 'attack';
    // Walk ONLY while actually stepping this frame — a mob that has reached the
    // player and is standing in range waiting out its cooldown played the walk
    // cycle in place, which read as marching / jumping on the spot. Standing still
    // now plays idle.
    if (m.moved) return 'walk';
    return 'idle';
  };

  const face = (m: MobEntity, dx: number, dy: number) => {
    if (Math.abs(dx) > Math.abs(dy)) { m.facing = 'side'; m.flipped = dx < 0; }
    else { m.facing = dy < 0 ? 'up' : 'down'; m.flipped = false; }
  };

  const drawFrames = (m: MobEntity, dt: number) => {
    const anim = anims(m);
    const dir: Facing = anim === 'death' ? 'side' : m.facing;
    const frames = tex.get(m.stat.id, anim, dir) ?? tex.get(m.stat.id, 'idle', m.facing);
    if (!frames) return;
    const fps = (ANIM_FPS as Record<string, number>)[anim] ?? 6;
    // Restart the cycle whenever the anim CHANGES — otherwise an attack/hit began
    // partway through (at whatever frame walk/idle happened to be on), which made
    // the swing pop/jump instead of playing cleanly from its wind-up frame.
    if (anim !== m.curAnim) { m.curAnim = anim; m.frame = 0; m.animT = 0; }
    m.animT += dt;
    if (m.animT >= 1 / fps) { m.animT = 0; m.frame++; }
    // The death fall plays ONCE and holds on the collapsed frame (looping it made
    // the mob "fall" repeatedly). Everything else loops.
    const idx = anim === 'death' ? Math.min(m.frame, frames.length - 1) : m.frame % frames.length;
    const tx = frames[idx];
    m.sprite.texture = tx;
    // Keep the full per-species scale on X (was reset to a bare ±PX every frame,
    // dropping MOB_HERO_MULT × MOB_SCALE — which squished every mob to a sliver and
    // made the sprites look broken). Mirror by sign only.
    const sc = PX * MOB_HERO_MULT * (MOB_SCALE[m.stat.id] ?? 1);
    m.sprite.scale.x = (m.flipped ? -1 : 1) * sc;
    m.sprite.scale.y = sc;
    // Frame sizes are mixed: idle/walk/hit/death are 32px, but attack blocks are
    // 64px (room for the weapon arc), with the body's FEET sitting higher in the
    // taller frame. A fixed anchor made the mob visibly "jump up" the instant it
    // swung. Anchor per frame height so the feet stay pinned to m.y either way.
    m.sprite.anchor.set(0.5, tx.height >= 64 ? 0.70 : 0.9);
  };

  const dist = (ax: number, ay: number, bx: number, by: number) => Math.hypot(ax - bx, ay - by) / TILE;

  return {
    entities,
    count: () => entities.filter((m) => m.state !== 'dying').length,
    spawn(stat, n, px, py, litZones, grid) {
      for (let i = 0; i < n && this.count() < tuning.maxAlive; i++) {
        // A ring around the player, outside lit zones and the min radius — and on
        // GROUND the mob could actually stand on.
        //
        // The nav grid was accepted here and then ignored, so the ring was purely
        // geometric: any angle that happened to point out to sea spawned an orc
        // standing on open water (or inside a cliff), where it then swam at you.
        // A spawn point has to be somewhere the thing could have walked from.
        let x = 0, y = 0, ok = false;
        for (let tries = 0; tries < 24 && !ok; tries++) {
          const ang = Math.random() * Math.PI * 2;
          const r = rand(tuning.spawnMinTiles, tuning.spawnMaxTiles) * TILE;
          x = px + Math.cos(ang) * r;
          y = py + Math.sin(ang) * r;
          if (litZones.some((z) => dist(x, y, z.x, z.y) < tuning.litZoneTiles)) continue;
          ok = !grid.blocked(Math.floor(x / TILE), Math.floor(y / TILE));
        }
        // 24 tries and still nothing standable (the player is on a headland, or
        // ringed by cliffs) — skip this one rather than drop it in the sea.
        if (!ok) continue;
        const frames = tex.get(stat.id, 'idle', 'down');
        if (!frames) continue;
        const sprite = new Sprite(frames[0]);
        sprite.anchor.set(0.5, 0.9);
        sprite.scale.set(PX * MOB_HERO_MULT * (MOB_SCALE[stat.id] ?? 1));
        sprite.x = x; sprite.y = y; sprite.zIndex = y;
        sprite.eventMode = 'static';
        sprite.cursor = 'crosshair';
        sprite.hitArea = new Rectangle(-16, -30, 32, 32);   // local coords → scales with the sprite
        sprite.alpha = 0;
        mount.addChild(sprite);
        entities.push({
          id: `m${seq++}`, stat, sprite, hp: stat.hp, state: 'spawning',
          x, y, spawnX: x, spawnY: y,
          facing: 'down', flipped: false, path: [], waypoint: null,
          animT: 0, frame: 0, curAnim: 'idle', moved: false, hitMs: 0, dyingMs: 0, killed: false, spawnMs: SPAWN_FLICKER_MS,
          attackCdMs: 0, loseMs: 0, attacking: false,
          dashCdMs: stat.dash?.everyMs ?? 0, dashMs: 0,
        });
      }
    },
    update(dt, px, py, isDark, grid, now, fearless = false, safe = false) {
      // Two different reasons for the same outcome — a fearless mount and standing on
      // your own safe ground both mean "nothing touches you" — so they collapse into
      // one flag rather than duplicating every guard below.
      const untouchable = fearless || safe;
      for (let i = entities.length - 1; i >= 0; i--) {
        const m = entities[i];
        const st = m.stat;

        if (m.hitMs > 0) m.hitMs -= dt * 1000;
        if (m.attackCdMs > 0) m.attackCdMs -= dt * 1000;
        if (m.dashCdMs > 0) m.dashCdMs -= dt * 1000;
        if (m.dashMs > 0) m.dashMs -= dt * 1000;

        // Dawn: dissolve and drop out. Also the death animation path.
        if (m.state === 'dying') {
          m.dyingMs -= dt * 1000;
          // A killed mob stays fully visible while it falls + lies collapsed, then
          // fades over the last 400ms. The dawn dissolve keeps its gradual fade.
          m.sprite.alpha = m.killed ? Math.min(1, m.dyingMs / 400) : Math.max(0, m.dyingMs / tuning.dissolveMs);
          drawFrames(m, dt);
          if (m.dyingMs <= 0) { m.sprite.destroy(); entities.splice(i, 1); }
          continue;
        }
        if (!isDark) { m.state = 'dying'; m.dyingMs = tuning.dissolveMs; continue; }

        // Flicker in on appearance (the pre-death-style resolve, reused).
        if (m.state === 'spawning') {
          m.spawnMs -= dt * 1000;
          m.sprite.alpha = m.spawnMs > 0 ? (Math.sin(now / 40) * 0.5 + 0.5) : 1;
          if (m.spawnMs <= 0) { m.sprite.alpha = 1; m.state = 'idle'; }
          drawFrames(m, dt);
          m.sprite.zIndex = m.y;
          continue;
        }

        const dToPlayer = dist(m.x, m.y, px, py);
        const dFromSpawn = dist(m.x, m.y, m.spawnX, m.spawnY);

        // Aggro / de-aggro. Chase within aggro range. Give up and walk home when the
        // player either (a) leaves aggro range for loseTargetMs — so stepping out of
        // its area sends it back to spawn instead of freezing in place — or (b) is led
        // more than deAggroTiles from the SPAWN point (can't be dragged across the map).
        // Fearless mount (heavy horse): night mobs won't aggro, and any current
        // chaser breaks off and heads home — you ride through the dark untouched.
        if (untouchable && m.state === 'chase') { m.state = 'return'; m.loseMs = 0; m.path = []; m.waypoint = null; }
        if (!untouchable && m.state === 'idle' && dToPlayer <= st.aggroTiles) { m.state = 'chase'; m.loseMs = 0; events.onAggro?.(m); }
        if (m.state === 'chase') {
          const tooFar = dFromSpawn > tuning.deAggroTiles;
          // Hysteresis: keep the player once aggro'd out past the aggro range, so the
          // mob doesn't flicker between chase/return right at the edge. 1.4→1.15:
          // 1.4× turned an already-generous aggro radius into an even wider *sticky*
          // radius, so walking to the edge of aggro never actually broke the chase.
          const outOfRange = dToPlayer > st.aggroTiles * 1.15;
          if (outOfRange) m.loseMs += dt * 1000; else m.loseMs = 0;
          if (tooFar || m.loseMs >= tuning.loseTargetMs) {
            m.state = 'return'; m.loseMs = 0; m.path = []; m.waypoint = null;
          }
        }

        // Attack windows.
        const isRanged = st.behaviour === 'ranged' || st.behaviour === 'kiter';
        const reach = isRanged ? (st.ranged?.range ?? 6) : 1.3;
        if (!untouchable && !m.attacking && m.state === 'chase' && dToPlayer <= reach && m.attackCdMs <= 0) {
          m.attacking = true;
          face(m, px - m.x, py - m.y);
          // Lock the mob in place for exactly as long as its swing/shot animation
          // runs, so the full cycle plays smoothly instead of being cut short mid-
          // frame (which snapped it back to idle and read as a jump). Clamped to a
          // sane range.
          const atkAnim = isRanged ? 'shoot' : 'attack';
          const atkFrames = tex.get(st.id, atkAnim, m.facing)?.length ?? 6;
          const atkFps = (ANIM_FPS as Record<string, number>)[atkAnim] ?? 12;
          const lockMs = Math.max(300, Math.min(900, Math.round((atkFrames / atkFps) * 1000)));
          m.attackCdMs = lockMs;
          const dmg = isRanged ? (st.ranged?.damage ?? st.damage) : st.damage;
          // Melee cadence 900→1500ms. Against 2-3 mobs a 0.9s swing each meant
          // near-continuous incoming damage with no gap to heal, retreat or land a
          // hit of your own; 1.5s leaves a readable window between swings.
          const every = isRanged ? (st.ranged?.everyMs ?? 2000) : 1500;
          // §2.1: telegraphed wind-up — the swing plays for WINDUP_MS before damage
          // applies, giving the player a window to react/dodge (damage on the
          // contact frame, not on animation start). Ranged fire is handled below.
          if (!isRanged) {
            events.onAttack?.(m);   // the swing is visible now; the damage lands after the wind-up
            window.setTimeout(() => { if (m.state !== 'dying') events.onHitPlayer(dmg); }, WINDUP_MS);
          } else {
            events.onFire?.(m, dmg);   // §7: ranged mobs spawn a visible projectile that deals the damage
          }
          m.attackCdMs = every;
          window.setTimeout(() => { m.attacking = false; }, lockMs);
        }

        // Movement (unless mid-swing). Reset the per-frame moved flag first.
        m.moved = false;
        if (!m.attacking) {
          let goalX = 0, goalY = 0, moving = false;
          if (m.state === 'chase') {
            // A kiter backs off if the player is too close.
            if (st.behaviour === 'kiter' && dToPlayer < 3) {
              goalX = m.x + (m.x - px); goalY = m.y + (m.y - py); moving = true;
            } else if (dToPlayer > reach * 0.85) {
              goalX = px; goalY = py; moving = true;
              // Dasher (orc): its base speed is just under a walking player's, so it
              // can't close a chase on its own. Every `everyMs` it lunges — a short
              // burst of ~2× speed that eats the gap even against a fleeing walker.
              if (st.behaviour === 'dasher' && st.dash && m.dashMs <= 0 && m.dashCdMs <= 0) {
                m.dashMs = DASH_DURATION_MS; m.dashCdMs = st.dash.everyMs;
              }
            }
          } else if (m.state === 'return') {
            // Walk back to spawn; re-aggro if the player wanders back within range.
            if (!untouchable && dToPlayer <= st.aggroTiles) { m.state = 'chase'; m.loseMs = 0; events.onAggro?.(m); }
            else if (dFromSpawn < 0.5) { m.state = 'idle'; }
            else { goalX = m.spawnX; goalY = m.spawnY; moving = true; }
          }

          if (moving) {
            // Re-plan occasionally via nav; between plans, step toward the waypoint.
            if (!m.waypoint || (!m.path.length && dist(m.x, m.y, m.waypoint.x, m.waypoint.y) < 0.6)) {
              const route = findRoute(
                grid,
                Math.floor(m.x / TILE), Math.floor(m.y / TILE),
                Math.floor(goalX / TILE), Math.floor(goalY / TILE),
                { maxExpand: 3000 },
              );
              m.path = route.tiles.map(([tx, ty]) => ({ x: (tx + 0.5) * TILE, y: (ty + 0.5) * TILE }));
              m.waypoint = m.path.shift() ?? { x: goalX, y: goalY };
            }
            const wp = m.waypoint!;
            const dx = wp.x - m.x, dy = wp.y - m.y;
            const d = Math.hypot(dx, dy);
            if (d < 2) { m.waypoint = m.path.shift() ?? null; }
            else {
              // Lunge burst sized so DASH_DURATION_MS at DASH_MULT× covers ~dash.tiles.
              const mult = m.dashMs > 0 ? DASH_MULT : 1;
              const step = st.speed * mult * TILE * dt;
              m.x += (dx / d) * step; m.y += (dy / d) * step;
              m.moved = step > 0.01;
              face(m, dx, dy);
            }
          }
        }

        // Chief calls adds once, below half HP.
        if (st.behaviour === 'chief' && st.callsAdds && m.hp <= st.hp / 2 && !(m as any)._called) {
          (m as any)._called = true;
          events.onCallAdds?.(m, st.callsAdds);
        }

        m.sprite.x = m.x; m.sprite.y = m.y; m.sprite.zIndex = m.y;
        m.sprite.tint = m.hitMs > 0 ? 0xff8888 : 0xffffff;
        // Only render a mob within the lit/visible area — one lurking out in the
        // remembered "grey" or the dark isn't shown (you don't track it), though it
        // keeps moving toward you and emerges into view when it enters the light.
        m.sprite.visible = dToPlayer <= MOB_VISIBLE_TILES;
        drawFrames(m, dt);
      }
    },
    hitAt(wx, wy, damage) {
      let best: MobEntity | null = null, bestD = Infinity;
      for (const m of entities) {
        if (m.state === 'dying' || m.state === 'spawning') continue;
        const dx = Math.abs(wx - m.x), dy = wy - m.y;
        if (dx > 34 || dy > 12 || dy < -70) continue;   // §2.1: widened for the larger mob silhouette
        const d = dx + Math.abs(dy);
        if (d < bestD) { bestD = d; best = m; }
      }
      if (!best) return null;
      best.hp -= damage;
      best.hitMs = HIT_FLASH_MS;
      if (best.state === 'idle') best.state = 'chase';   // hitting it aggros it
      if (best.hp <= 0) {
        best.state = 'dying';
        best.killed = true;
        best.dyingMs = DEATH_MS;                         // fall + lie collapsed, then fade
        best.frame = 0; best.animT = 0;                  // restart the death anim from frame 0
        best.sprite.eventMode = 'none';
        events.onDeath(best);
        return { entity: best, died: true };
      }
      return { entity: best, died: false };
    },
    destroy() {
      for (const m of entities) m.sprite.destroy();
      if (!host) ownLayer.destroy({ children: true });
      entities.length = 0;
    },
  };
}
